import { auroraStore } from '../../store/auroraStore'

/**
 * One tap, no talking.
 *
 * For the evening when a parent has nothing left. A day with a tap logged is
 * worth far more than a day with nothing, and the alternative to this row is
 * not a better note — it is silence.
 *
 * The wording is about the activity, not the child. "Hard going" describes a
 * ten minute stretch; it never becomes a verdict on her.
 */

const RATINGS = [
  { value: -1, label: 'Hard going', hint: 'It did not land today', tone: 'attention' },
  { value: 0, label: 'Steady', hint: 'About where she was', tone: 'steady' },
  { value: 1, label: 'Moved', hint: 'Something clicked', tone: 'progress' },
  { value: 2, label: 'First time', hint: 'She did it for the first time', tone: 'milestone' },
] as const

export function QuickRating({
  childId,
  blockRef,
  onDone,
}: {
  childId: string
  blockRef?: string
  onDone: () => void
}) {
  const log = (rating: number) => {
    ;(auroraStore.getState() as any).logQuickCapture({ childId, rating, blockRef })
    try { navigator.vibrate?.(10) } catch { /* not supported, not important */ }
    onDone()
  }

  return (
    <div className="quick" role="group" aria-label="Log how it went in one tap">
      {RATINGS.map((r) => (
        <button
          key={r.value}
          type="button"
          className={`quick-btn quick-${r.tone}`}
          onClick={() => log(r.value)}
        >
          <span className="quick-mark" aria-hidden="true">
            <Mark tone={r.tone} />
          </span>
          <span className="quick-label">{r.label}</span>
          <span className="quick-hint">{r.hint}</span>
        </button>
      ))}
    </div>
  )
}

const S = {
  viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  width: 18, height: 18, 'aria-hidden': true,
}

/** Direction of travel, drawn as a line — never a face, never a score. */
function Mark({ tone }: { tone: string }) {
  if (tone === 'attention') return <svg {...S}><path d="M4 8l6 6 4-4 6 6" /></svg>
  if (tone === 'steady') return <svg {...S}><path d="M4 12h16" /></svg>
  if (tone === 'progress') return <svg {...S}><path d="M4 16l6-6 4 4 6-6" /></svg>
  return (
    <svg {...S}>
      <path d="M12 3l2.3 6.2L21 11l-6.7 1.8L12 19l-2.3-6.2L3 11l6.7-1.8z" />
    </svg>
  )
}
